import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';

import type {
  GatewayProvider,
  PaymentCurrency,
  PaymentHistory,
  PaymentHistoryError,
} from '../../../types/payment';
import { isGatewayProvider, PAYMENT_SYSTEM_LABELS } from '../../../types/payment';
import {
  SubscriptionPlan,
  StudentSubscription,
  StudentSubscriptionPauseRecord,
  StudentSubscriptionTransition,
  TRAINING_CYCLE_LABELS,
  type TrainingCycle,
} from '../../../types';
import { StudentPaymentAccountService, PaymentHistoryService } from '../../../services/PaymentAccountService';
import { StudentSubscriptionService } from '../../../services';
import { PaymentGatewayService } from '../../../services/PaymentGatewayService';
import { PhoneService } from '../../../services/PhoneService';
import { formatDate, formatDateTime, formatCurrency } from '../../../utils';
import {
  addBillingCycleDate,
  isStudentSubscriptionActive,
  isStudentSubscriptionCanceled,
  pickDisplayStudentSubscription,
  resolvePlanTransitionEffectiveStartDate,
} from '../../../utils/subscriptionLifecycle';
import { validateCpf } from '../../../utils/validation';
import { copyTextToClipboard } from '../../../utils/clipboard';
import { getStudentInactivationReasonLabel } from '../../../constants/studentInactivationReason';
import { usePaymentHistory } from '../../../hooks/usePaymentHistory';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import { useAuth } from '../../../contexts/AuthContext';
import {
  canManageStudentSubscriptions,
  canManageSubscriptionPlans,
  canPauseSubscription,
} from '../../../utils/permissions';
import { getScheduledResumeDate } from '../../../utils/subscriptionPause';
import Icon from '../../common/Icon/Icon';
import EmptyState from '../../common/EmptyState/EmptyState';
import DataTable from '../../common/DataTable/DataTable';
import SearchFilterBar from '../../common/SearchFilterBar/SearchFilterBar';
import Button from '../../common/Button/Button';
import KebabMenu from '../../common/KebabMenu/KebabMenu';
import Loading from '../../common/Loading/Loading';
import FormSelect from '../../common/FormSelect/FormSelect';

import SummaryPills from '../../common/SummaryPills/SummaryPills';
import FilterBar from '../../common/FilterBar/FilterBar';
import FilterGroup from '../../common/FilterGroup/FilterGroup';
import MultiSelectFilter from '../../common/MultiSelectFilter/MultiSelectFilter';
import ClearFiltersButton from '../../common/ClearFiltersButton/ClearFiltersButton';
import FilterChipsBar from '../../common/FilterChipsBar/FilterChipsBar';
import type { FilterChipEntry } from '../../common/FilterChipsBar/FilterChipsBar';
import Pagination from '../../common/Pagination/Pagination';
import PageSizeSelect from '../../common/PageSizeSelect/PageSizeSelect';
import ConfirmModal from '../../common/ConfirmModal/ConfirmModal';
import ViewLinkButton from '../../common/ViewLinkButton/ViewLinkButton';
import ContentCard from '../../common/ContentCard/ContentCard';
import PlanNameWithProject from '../../common/PlanNameWithProject/PlanNameWithProject';
import Tooltip from '../../common/Tooltip';
import CreatePaymentAccountModal, { type CreatePaymentAccountSelection } from './CreatePaymentAccountModal';
import RegisterPixChargeModal, { type PixChargeStatus } from './RegisterPixChargeModal';
import LinkPaymentAccountModal from './LinkPaymentAccountModal';
import CreateSubscriptionModal from './CreateSubscriptionModal';
import CreateChargeModal from './CreateChargeModal';
import ConfirmPaymentModal from './ConfirmPaymentModal';
import SchedulePlanTransitionModal from './SchedulePlanTransitionModal';
import SubscriptionCancellationReasonModal from './SubscriptionCancellationReasonModal';
import PauseSubscriptionModal from './PauseSubscriptionModal';
import ResumeSubscriptionModal from './ResumeSubscriptionModal';
import SubscriptionHeaderActions from './SubscriptionHeaderActions';
import SubscriptionStatusCard from './SubscriptionStatusCard';
import StudentConsultancyCreditControl from '../StudentConsultancyCreditControl/StudentConsultancyCreditControl';
import ChargeCheckoutLinkModal from '../../../pages/Commercial/ChargeCheckoutLinkModal';
import { AsaasPayloadView, PayPalPayloadView, StripePayloadView } from './payloads';
import { AccountEntry, PaymentDetailsProps, PanelProps } from './paymentDetails.types';
import type { ExternalSubscription } from '../../../services/PaymentGatewayService';
import {
  CHARGE_TYPE_LABELS,
  ENDED_REASON_LABELS,
  STATUS_LABELS,
  STATUS_ICON,
  SUBSCRIPTION_PAYMENT_METHOD_LABELS,
  TRANSITION_STATUS_LABELS,
  normalizeStatus,
} from './paymentDetails.constants';
import styles from './PaymentDetails.module.css';

const TRAINING_CYCLE_OPTIONS = (
  Object.entries(TRAINING_CYCLE_LABELS) as Array<[TrainingCycle, string]>
).map(([value, label]) => ({ value, label }));

const WEBHOOK_SYNC_DELAY = 3000;
const OPEN_PAYMENT_STATUSES = new Set(['PENDING', 'EXPIRED']);
const CONFIRMABLE_PAYMENT_STATUSES = new Set(['PENDING', 'EXPIRED', 'FAILED', 'OVERDUE']);
const PAID_PAYMENT_STATUS = 'PAID';
const PAYMENT_PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;
const PAYMENT_STATUS_FILTER_VALUES = ['PAID', 'PENDING', 'EXPIRED', 'FAILED', 'REFUNDED', 'CANCELED', 'PAUSED', 'CREDIT', 'UNKNOWN'] as const;
const PAYMENT_CHARGE_TYPE_FILTER_VALUES = ['RECURRING', 'SINGLE'] as const;
const PAYMENT_QUERY_SCHEMA = {
  paymentAccount: queryFilter.integer(0, { min: 1 }),
  paymentPage: queryFilter.integer(1, { min: 1 }),
  paymentPageSize: queryFilter.integer(10, { allowed: PAYMENT_PAGE_SIZE_OPTIONS }),
  paymentSearch: queryFilter.string(),
  paymentStatus: queryFilter.array<string>([], { allowed: PAYMENT_STATUS_FILTER_VALUES }),
  paymentChargeType: queryFilter.array<string>([], { allowed: PAYMENT_CHARGE_TYPE_FILTER_VALUES }),
};
const PAYPAL_BA_TOKEN_MAX_AGE_MS = 72 * 60 * 60 * 1000;
const PAYPAL_APPROVAL_LINK_EXPIRED_MESSAGE = 'Não foi possível gerar um novo link de aprovação PayPal. Tente novamente ou recrie a assinatura.';
const PAYPAL_APPROVAL_LINK_REFRESHED_MESSAGE = 'Link de aprovação expirado. Uma nova assinatura PayPal foi criada e o link foi copiado.';
function isPayPalSubscriptionId(value: string | null | undefined): boolean {
  return typeof value === 'string' && /^I-[A-Z0-9]+$/i.test(value.trim());
}

function getPayPalRawStatus(payload: Record<string, unknown> | null): string | null {
  if (!payload) {
    return null;
  }

  if (typeof payload.paypalStatus === 'string' && payload.paypalStatus.trim()) {
    return payload.paypalStatus.trim().toUpperCase();
  }

  const subscription = isRecord(payload.paypal_subscription) ? payload.paypal_subscription : null;
  if (subscription && typeof subscription.status === 'string' && subscription.status.trim()) {
    return subscription.status.trim().toUpperCase();
  }

  return null;
}

function isPayPalApprovalPending(payload: Record<string, unknown> | null): boolean {
  return getPayPalRawStatus(payload) === 'APPROVAL_PENDING';
}

function parseGatewayTimestamp(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }

  const normalized = value.includes('T') ? value : `${value}T00:00:00`;
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

function getPayPalApprovalReferenceTimestamp(
  ph: PaymentHistory,
  payload: Record<string, unknown> | null,
): number | null {
  const candidates: Array<number | null> = [
    parseGatewayTimestamp(ph.external_created_date),
    parseGatewayTimestamp(ph.created_at),
  ];

  const subscription = payload && isRecord(payload.paypal_subscription) ? payload.paypal_subscription : null;
  if (subscription) {
    candidates.push(parseGatewayTimestamp(typeof subscription.create_time === 'string' ? subscription.create_time : null));
    candidates.push(parseGatewayTimestamp(typeof subscription.start_time === 'string' ? subscription.start_time : null));
  }

  const validCandidates = candidates.filter((value): value is number => typeof value === 'number');
  return validCandidates.length > 0 ? Math.min(...validCandidates) : null;
}

function isPayPalApprovalLinkLikelyExpired(
  ph: PaymentHistory,
  payload: Record<string, unknown> | null,
): boolean {
  const referenceTimestamp = getPayPalApprovalReferenceTimestamp(ph, payload);
  if (referenceTimestamp === null) {
    return false;
  }

  return Date.now() - referenceTimestamp > PAYPAL_BA_TOKEN_MAX_AGE_MS;
}

function isPayPalPendingApprovalCharge(
  ph: PaymentHistory,
  payload: Record<string, unknown> | null,
): boolean {
  if (isPayPalApprovalPending(payload)) {
    return true;
  }

  if (ph.charge_type !== 'RECURRING' || normalizeStatus(ph.status) !== 'PENDING') {
    return false;
  }

  return isPayPalSubscriptionId(ph.external_payment_id) || isPayPalSubscriptionId(ph.external_subscription_id);
}

function resolvePayPalSubscriptionIdForLookup(
  ph: PaymentHistory,
  subscription: StudentSubscription | null | undefined,
): string | null {
  const candidates = [
    ph.external_subscription_id,
    isPayPalSubscriptionId(ph.external_payment_id) ? ph.external_payment_id : null,
    subscription?.external_subscription_id ?? null,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isCanceledCharge(status: string): boolean {
  return normalizeStatus(status) === 'CANCELED';
}

function extractTransactionReceiptUrl(payload: Record<string, unknown> | null): string | null {
  if (!payload) {
    return null;
  }

  const payment = isRecord(payload.payment) ? payload.payment : payload;
  const url = payment.transactionReceiptUrl;
  return typeof url === 'string' && url.trim() ? url.trim() : null;
}

function unwrapGatewayPayload(value: unknown): Record<string, unknown> | null {
  if (!isRecord(value)) {
    return null;
  }

  let current: Record<string, unknown> | null = value;

  for (let depth = 0; depth < 6 && current; depth += 1) {
    const hasPaymentLikeFields = [
      'id',
      'object',
      'status',
      'payment',
      'description',
      'value',
      'dueDate',
      'dateCreated',
      'currency',
      'invoiceUrl',
      'bankSlipUrl',
      'hosted_invoice_url',
      'paypal_invoice',
      'paypal_subscription',
      'paypal_transaction',
      'charge_type',
      'invoiceNumber',
    ].some((key) => key in current!);

    if (hasPaymentLikeFields) {
      return current;
    }

    if (isRecord(current.payload)) {
      current = current.payload as Record<string, unknown>;
      continue;
    }

    if (isRecord(current.data)) {
      current = current.data as Record<string, unknown>;
      continue;
    }

    return current;
  }

  return current;
}

function extractPaymentUrlFromPayload(payload: Record<string, unknown> | null): string | null {
  if (!payload) {
    return null;
  }

  const payment = isRecord(payload.payment) ? payload.payment : payload;

  for (const key of ['url', 'invoiceUrl', 'hosted_invoice_url', 'bankSlipUrl']) {
    const value = payment[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

function normalizeGatewayPaymentDetail(detail: unknown): { url: string | null; payload: Record<string, unknown> | null } {
  const root = isRecord(detail) ? detail : null;
  const nestedData = root && isRecord(root.data) ? (root.data as Record<string, unknown>) : null;

  let url: string | null = null;
  for (const source of [root, nestedData]) {
    if (source && typeof source.url === 'string' && source.url.trim()) {
      url = source.url;
      break;
    }
  }

  let payload: Record<string, unknown> | null = null;
  for (const source of [root, nestedData]) {
    if (!source) continue;
    if (isRecord(source.payload)) {
      payload = unwrapGatewayPayload(source.payload);
      break;
    }
  }

  payload = payload ?? unwrapGatewayPayload(root);

  if (!url && payload && typeof payload.url === 'string' && payload.url.trim()) {
    url = payload.url;
  }

  if (!url) {
    url = extractPaymentUrlFromPayload(payload);
  }

  return { url, payload };
}

function buildFallbackPayloadFromPaymentHistory(
  ph: PaymentHistory,
  paymentLinkUrl: string | null,
): Record<string, unknown> {
  return {
    id: ph.external_payment_id,
    status: ph.status,
    value: ph.value,
    currency: ph.currency ?? 'BRL',
    dueDate: ph.due_date,
    paymentDate: ph.paid_at,
    dateCreated: ph.external_created_date ?? ph.created_at,
    charge_type: ph.charge_type,
    external_subscription_id: ph.external_subscription_id,
    invoiceUrl: paymentLinkUrl,
  };
}

function hasPersistablePayload(payload: Record<string, unknown> | null): payload is Record<string, unknown> {
  return Boolean(payload && Object.keys(payload).length > 0);
}

function hasVisiblePaymentError(payment: PaymentHistory): boolean {
  return Boolean(payment.latest_error) && normalizeStatus(payment.status) !== 'PAID';
}

function getGatewayLabel(provider: string): string {
  return PAYMENT_SYSTEM_LABELS[provider as keyof typeof PAYMENT_SYSTEM_LABELS] ?? provider;
}

function getTrainingCycleLabel(value: TrainingCycle | string | null | undefined): string {
  if (value === 'bimonthly') {
    return TRAINING_CYCLE_LABELS.bimonthly;
  }

  return TRAINING_CYCLE_LABELS.monthly;
}

function resolveTrainingCycleValue(value: TrainingCycle | string | null | undefined): TrainingCycle {
  return value === 'bimonthly' ? 'bimonthly' : 'monthly';
}

function isTrainingCycleValue(value: string): value is TrainingCycle {
  return value === 'monthly' || value === 'bimonthly';
}

function getDateOnlyTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(`${value}T00:00:00`);
  return Number.isNaN(parsed) ? null : parsed;
}

function sortPaymentsByDueDateDesc(payments: PaymentHistory[]): PaymentHistory[] {
  return [...payments].sort((leftPayment, rightPayment) => {
    const leftDueDate = getDateOnlyTimestamp(leftPayment.due_date);
    const rightDueDate = getDateOnlyTimestamp(rightPayment.due_date);

    if (leftDueDate === null && rightDueDate === null) return 0;
    if (leftDueDate === null) return 1;
    if (rightDueDate === null) return -1;

    return rightDueDate - leftDueDate;
  });
}

function resolveUpcomingChargeDate(
  payments: PaymentHistory[],
  billingCycle: string | null,
  subscription: PanelProps['subscription'],
): string | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTimestamp = today.getTime();

  const scheduledPayments = payments
    .filter((payment) => OPEN_PAYMENT_STATUSES.has(normalizeStatus(payment.status)) && payment.due_date)
    .sort((left, right) => (getDateOnlyTimestamp(left.due_date) ?? Number.MAX_SAFE_INTEGER) - (getDateOnlyTimestamp(right.due_date) ?? Number.MAX_SAFE_INTEGER));

  const nextScheduled = scheduledPayments.find((payment) => {
    const timestamp = getDateOnlyTimestamp(payment.due_date);
    return timestamp !== null && timestamp > todayTimestamp;
  });

  if (nextScheduled?.due_date) {
    return nextScheduled.due_date;
  }

  if (!billingCycle || !subscription?.start_date) {
    return null;
  }

  if (subscription.is_single_payment) {
    return null;
  }

  let candidate = subscription.start_date;

  while (getDateOnlyTimestamp(candidate) !== null && (getDateOnlyTimestamp(candidate) ?? 0) <= todayTimestamp) {
    const next = addBillingCycleDate(candidate, billingCycle);
    if (next === candidate) {
      return null;
    }
    candidate = next;
  }

  return candidate;
}

function getSubscriptionSnapshotDescription(subscription: PanelProps['subscription']): string | null {
  const description = subscription?.plan_description?.trim();
  return description || null;
}

function calcLoyaltyProgress(startDate: string | null | undefined): number {
  if (!startDate) return 1;
  const start = new Date(startDate);
  const now = new Date();
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  return Math.max(1, months + 1);
}

function formatLoyaltyPeriod(months: number): string {
  if (months <= 0) return 'Sem fidelidade';
  if (months === 1) return '1 mês';
  return `${months} meses`;
}

function formatLoyaltyPlanLabel(months: number): string {
  if (months <= 0) return 'Sem fidelidade';
  return `Fidelidade ${formatLoyaltyPeriod(months)}`;
}

function formatLoyaltyProgressLabel(
  startDate: string | null | undefined,
  loyaltyMonths: number,
): string {
  if (loyaltyMonths <= 0) return 'Sem fidelidade';
  return `${Math.min(calcLoyaltyProgress(startDate), loyaltyMonths)} de ${formatLoyaltyPeriod(loyaltyMonths)}`;
}

function getSubscriptionCreatedAtTimestamp(entry: AccountEntry): number {
  const createdAt = entry.subscription?.created_at;
  if (!createdAt) {
    return Number.MIN_SAFE_INTEGER;
  }

  const timestamp = Date.parse(createdAt);
  return Number.isNaN(timestamp) ? Number.MIN_SAFE_INTEGER : timestamp;
}

function getPreferredAccountIndex(entries: AccountEntry[]): number {
  if (entries.length === 0) {
    return 0;
  }

  let preferredActiveIndex = -1;
  let preferredActiveCreatedAt = Number.MIN_SAFE_INTEGER;
  let preferredLatestIndex = -1;
  let preferredLatestCreatedAt = Number.MIN_SAFE_INTEGER;

  entries.forEach((entry, index) => {
    if (!entry.subscription) {
      return;
    }

    const createdAt = getSubscriptionCreatedAtTimestamp(entry);

    if (isStudentSubscriptionActive(entry.subscription) && createdAt > preferredActiveCreatedAt) {
      preferredActiveCreatedAt = createdAt;
      preferredActiveIndex = index;
    }

    if (createdAt > preferredLatestCreatedAt) {
      preferredLatestCreatedAt = createdAt;
      preferredLatestIndex = index;
    }
  });

  if (preferredActiveIndex >= 0) {
    return preferredActiveIndex;
  }

  if (preferredLatestIndex >= 0) {
    return preferredLatestIndex;
  }

  return 0;
}

const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  studentId,
  studentName,
  studentCpf,
  studentEmail,
  canManageConsultancyCredit = false,
}) => {
  const { roles } = useAuth();
  const canManagePayments = canManageStudentSubscriptions(roles);
  const [entries, setEntries] = useState<AccountEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkModalMode, setLinkModalMode] = useState<'link' | 'change'>('link');
  const [isLinkingAccount, setIsLinkingAccount] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [studentPhone, setStudentPhone] = useState<string | null>(null);
  const {
    filters: paymentQuery,
    setFilter: setPaymentQueryFilter,
  } = useQueryFilters(PAYMENT_QUERY_SCHEMA, { pageKey: 'paymentPage' });

  useEffect(() => {
    PhoneService.findByStudentId(studentId).then((phone) => {
      if (phone?.phone_number && phone?.country_code) {
        setStudentPhone(`${phone.country_code}${phone.phone_number}`);
      }
    });
  }, [studentId]);

  const loadEntries = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const junctions = await StudentPaymentAccountService.findAllByStudentIdWithAccount(studentId);
      const loaded = await Promise.all(
        junctions.map(async (j) => {
          const subscriptions = await StudentSubscriptionService
            .findAllLinkedByStudentJunction(studentId, j.id)
            .catch(() => []);
          const subscription = pickDisplayStudentSubscription(subscriptions);

          return {
            junction: j,
            subscription,
          };
        }),
      );

      setEntries(loaded);
    } catch {
      setEntries([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [studentId]);

  const refreshEntries = useCallback(() => loadEntries(false), [loadEntries]);

  const accountOptions = useMemo(
    () => entries.map((entry) => ({
      value: String(entry.junction.payment_accounts.id),
      label: entry.junction.payment_accounts.payment_system === 'pix'
        ? `${studentName || entry.junction.payment_accounts.name || 'Aluna'} - ${PAYMENT_SYSTEM_LABELS.pix}`
        : `${studentName || entry.junction.payment_accounts.name || 'Aluna'} - ${PAYMENT_SYSTEM_LABELS[entry.junction.payment_accounts.payment_system] ?? entry.junction.payment_accounts.payment_system} (${entry.junction.payment_accounts.external_customer_id})`,
    })),
    [entries, studentName],
  );

  const selectedIndex = useMemo(() => {
    const persistedIndex = entries.findIndex(
      (entry) => entry.junction.payment_accounts.id === paymentQuery.paymentAccount,
    );
    return persistedIndex >= 0 ? persistedIndex : getPreferredAccountIndex(entries);
  }, [entries, paymentQuery.paymentAccount]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleModalConfirm = async (selection: CreatePaymentAccountSelection) => {
    setIsSubmitting(true);
    try {
      if (selection.system === 'pix') {
        await PaymentGatewayService.createPixAccount(studentId, selection.planId);
      } else {
        await PaymentGatewayService.createCustomer(selection.system, studentId);
      }
      await loadEntries();
      setIsSubmitting(false);
      setIsModalOpen(false);
      setWarning(null);
    } catch (err) {
      setIsSubmitting(false);
      setIsModalOpen(false);
      setWarning(err instanceof Error ? err.message : 'Algo deu errado. Tente novamente.');
    }
  };

  const handleLinkAccountConfirm = async (paymentAccountId: number) => {
    const currentEntry = entries[selectedIndex] ?? null;
    const currentJunctionId = currentEntry?.junction.id ?? null;
    const isChangingAccount = linkModalMode === 'change' && currentJunctionId !== null;

    setIsLinkingAccount(true);
    try {
      if (isChangingAccount && currentJunctionId) {
        await PaymentGatewayService.replaceStudentPaymentAccount(
          studentId,
          currentJunctionId,
          paymentAccountId,
        );
      } else {
        await PaymentGatewayService.linkStudentPaymentAccount(studentId, paymentAccountId);
      }

      await loadEntries();
      setIsLinkModalOpen(false);
      setWarning(null);
    } catch (err) {
      setWarning(err instanceof Error ? err.message : 'Não foi possível atualizar a conta de pagamento.');
    } finally {
      setIsLinkingAccount(false);
    }
  };

  const openLinkAccountModal = (mode: 'link' | 'change') => {
    setWarning(null);
    setLinkModalMode(mode);
    setIsLinkModalOpen(true);
  };

  if (loading) return <Loading />;

  return (
    <div className={styles['payment-section']}>
      {warning && <div className={styles['alert-warning']}>{warning}</div>}

      {entries.length === 0 ? (
        <div className={styles['empty-state-wrap']}>
          <EmptyState
            message="Nenhuma conta de pagamento vinculada a esta aluna."
            icon="payment"
          />
          {canManagePayments && (
            <div className={styles['empty-state-actions']}>
              <button
                type="button"
                className={styles['create-btn']}
                onClick={() => { setWarning(null); setIsModalOpen(true); }}
              >
                Criar conta de pagamento
              </button>
              <button
                type="button"
                className={styles['link-btn']}
                onClick={() => openLinkAccountModal('link')}
              >
                Vincular conta de pagamento
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {entries.length > 0 && (
            <div className={styles['account-select-wrap']}>
              <FormSelect
                id="payment-details-account-select"
                options={accountOptions}
                value={String(entries[selectedIndex].junction.payment_accounts.id)}
                onChange={(e) => setPaymentQueryFilter('paymentAccount', Number(e.target.value))}
                placeholder="Selecione a conta"
                containerClassName={styles['account-select-field']}
                className={styles['account-select-trigger']}
                disabled={entries.length === 1}
              />
            </div>
          )}
          <PaymentAccountPanel
            studentId={studentId}
            studentName={studentName}
            account={entries[selectedIndex].junction.payment_accounts}
            junctionId={entries[selectedIndex].junction.id}
            subscription={entries[selectedIndex].subscription}
            studentPhone={studentPhone}
            studentCpf={studentCpf}
            onRefresh={refreshEntries}
            onChangeAccount={() => openLinkAccountModal('change')}
            canChangeAccount={!entries[selectedIndex].subscription || !isStudentSubscriptionActive(entries[selectedIndex].subscription)}
            canManageConsultancyCredit={canManageConsultancyCredit}
          />
        </>
      )}

      <CreatePaymentAccountModal
        open={isModalOpen}
        loading={isSubmitting}
        onConfirm={handleModalConfirm}
        onCancel={() => setIsModalOpen(false)}
        studentName={studentName}
        studentCpf={studentCpf}
        studentEmail={studentEmail}
      />

      <LinkPaymentAccountModal
        open={isLinkModalOpen}
        loading={isLinkingAccount}
        studentId={studentId}
        currentPaymentAccountId={linkModalMode === 'change'
          ? entries[selectedIndex]?.junction.payment_accounts.id ?? null
          : null}
        title={linkModalMode === 'change' ? 'Alterar conta de pagamento' : 'Vincular conta de pagamento'}
        message={linkModalMode === 'change'
          ? 'Selecione outra conta com assinatura ativa já cadastrada no portal para substituir o vínculo atual da aluna.'
          : 'Busque uma conta com assinatura ativa já cadastrada no portal para reutilizar o mesmo cliente no gateway.'}
        confirmLabel={linkModalMode === 'change' ? 'Alterar conta' : 'Vincular conta'}
        onConfirm={(account) => { void handleLinkAccountConfirm(account.id); }}
        onCancel={() => setIsLinkModalOpen(false)}
      />
    </div>
  );
};

const PaymentAccountPanel: React.FC<PanelProps> = ({
  studentId,
  studentName,
  account,
  junctionId,
  subscription,
  studentPhone,
  studentCpf,
  onRefresh,
  onChangeAccount,
  canManageConsultancyCredit = false,
}) => {
  const [warning, setWarning] = useState<string | null>(null);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [isCreateChargeModalOpen, setIsCreateChargeModalOpen] = useState(false);
  const [isCreatingCharge, setIsCreatingCharge] = useState(false);
  const [isPixChargeModalOpen, setIsPixChargeModalOpen] = useState(false);
  const [isRegisteringPixCharge, setIsRegisteringPixCharge] = useState(false);
  const [isPixPlanModalOpen, setIsPixPlanModalOpen] = useState(false);
  const [isCreatingPixSubscription, setIsCreatingPixSubscription] = useState(false);
  const [isChargeCheckoutModalOpen, setIsChargeCheckoutModalOpen] = useState(false);
  const [isCancelReasonModalOpen, setIsCancelReasonModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [subscriptionCancellationReason, setSubscriptionCancellationReason] = useState('');
  const [subscriptionCancellationObservation, setSubscriptionCancellationObservation] = useState<string | null>(null);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [subscriptions, setSubscriptions] = useState<ExternalSubscription[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [allHistory, setAllHistory] = useState<PaymentHistory[]>([]);
  const [paymentToCancel, setPaymentToCancel] = useState<PaymentHistory | null>(null);
  const [isCancellingPayment, setIsCancellingPayment] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<PaymentHistory | null>(null);
  const [isDeletingPayment, setIsDeletingPayment] = useState(false);
  const [paymentToRefund, setPaymentToRefund] = useState<PaymentHistory | null>(null);
  const [isRefundingPayment, setIsRefundingPayment] = useState(false);
  const [paymentToConfirm, setPaymentToConfirm] = useState<PaymentHistory | null>(null);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [copiedPaymentId, setCopiedPaymentId] = useState<number | null>(null);
  const [copyingPaymentId, setCopyingPaymentId] = useState<number | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentHistory | null>(null);
  const [selectedPaymentError, setSelectedPaymentError] = useState<PaymentHistoryError | null>(null);
  const [payloadDetail, setPayloadDetail] = useState<Record<string, unknown> | null>(null);
  const [payloadLoading, setPayloadLoading] = useState(false);
  const [isPlanTransitionModalOpen, setIsPlanTransitionModalOpen] = useState(false);
  const [isSchedulingPlanTransition, setIsSchedulingPlanTransition] = useState(false);
  const [scheduledTransition, setScheduledTransition] = useState<StudentSubscriptionTransition | null>(null);
  const [scheduledTransitionLoading, setScheduledTransitionLoading] = useState(false);
  const [isCancelTransitionModalOpen, setIsCancelTransitionModalOpen] = useState(false);
  const [isCancellingScheduledTransition, setIsCancellingScheduledTransition] = useState(false);
  const [activePause, setActivePause] = useState<StudentSubscriptionPauseRecord | null>(
    subscription?.active_pause ?? null,
  );
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [isPausingSubscription, setIsPausingSubscription] = useState(false);
  const [isResumingSubscription, setIsResumingSubscription] = useState(false);
  const [isUpdatingTrainingCycle, setIsUpdatingTrainingCycle] = useState(false);
  const [isEditingTrainingCycle, setIsEditingTrainingCycle] = useState(false);
  const { roles } = useAuth();
  const canManagePayments = canManageStudentSubscriptions(roles);
  const canManagePause = canPauseSubscription(roles);

  const pendingDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const creatingChargeRef = useRef(false);

  const {
    filters: paymentQuery,
    setFilter: setPaymentQueryFilter,
    setFilters: setPaymentQueryFilters,
  } = useQueryFilters(PAYMENT_QUERY_SCHEMA, { pageKey: 'paymentPage' });
  const {
    paymentPage,
    paymentPageSize,
    paymentSearch: searchTerm,
    paymentStatus: statusFilter,
    paymentChargeType: chargeTypeFilter,
  } = paymentQuery;

  const historyFilters = useMemo(() => ({
    search: searchTerm || undefined,
    status: statusFilter.length > 0 ? statusFilter : undefined,
    chargeType: chargeTypeFilter.length > 0 ? chargeTypeFilter : undefined,
  }), [searchTerm, statusFilter, chargeTypeFilter]);

  const {
    history,
    summary,
    pagination,
    loading: historyLoading,
    silentRefresh,
    optimisticUpdate,
  } = usePaymentHistory(
    account.id,
    null,
    paymentPage,
    paymentPageSize,
    historyFilters,
  );

  const sortedHistory = useMemo(
    () => sortPaymentsByDueDateDesc(history),
    [history],
  );

  const statusOptions = useMemo(() => [
    { value: 'PAID', label: 'Pago', icon: STATUS_ICON.PAID?.name, iconColor: STATUS_ICON.PAID?.color },
    { value: 'PENDING', label: 'Pendente', icon: STATUS_ICON.PENDING?.name, iconColor: STATUS_ICON.PENDING?.color },
    { value: 'EXPIRED', label: 'Vencido', icon: STATUS_ICON.EXPIRED?.name, iconColor: STATUS_ICON.EXPIRED?.color },
    { value: 'FAILED', label: 'Falha', icon: STATUS_ICON.FAILED?.name, iconColor: STATUS_ICON.FAILED?.color },
    { value: 'REFUNDED', label: 'Reembolsado', icon: STATUS_ICON.REFUNDED?.name, iconColor: STATUS_ICON.REFUNDED?.color },
    { value: 'CANCELED', label: 'Cancelado', icon: STATUS_ICON.CANCELED?.name, iconColor: STATUS_ICON.CANCELED?.color },
    { value: 'PAUSED', label: 'Pausado', icon: STATUS_ICON.PAUSED?.name, iconColor: STATUS_ICON.PAUSED?.color },
    { value: 'CREDIT', label: 'Crédito', icon: STATUS_ICON.CREDIT?.name, iconColor: STATUS_ICON.CREDIT?.color },
    { value: 'UNKNOWN', label: 'Desconhecido', icon: STATUS_ICON.UNKNOWN?.name, iconColor: STATUS_ICON.UNKNOWN?.color },
  ], []);

  const chargeTypeOptions = useMemo(() => [
    { value: 'RECURRING', label: 'Recorrente' },
    { value: 'SINGLE', label: 'À vista' },
  ], []);

  const allOptionsMap = useMemo(() => {
    const map: Record<string, Record<string, { label: string; icon?: string; iconColor?: string }>> = {};
    map.status = {};
    for (const o of statusOptions) map.status[o.value] = o;
    map.chargeType = {};
    for (const o of chargeTypeOptions) map.chargeType[o.value] = o;
    return map;
  }, [statusOptions, chargeTypeOptions]);

  const handleStatusFilterChange = useCallback((newValues: string[]) => {
    setPaymentQueryFilter('paymentStatus', newValues);
  }, [setPaymentQueryFilter]);

  const handleChargeTypeFilterChange = useCallback((newValues: string[]) => {
    setPaymentQueryFilter('paymentChargeType', newValues);
  }, [setPaymentQueryFilter]);

  const hasActiveFilters = !!(searchTerm || statusFilter.length > 0 || chargeTypeFilter.length > 0);

  const handleClearFilters = useCallback(() => {
    setPaymentQueryFilters({
      paymentSearch: '',
      paymentStatus: [],
      paymentChargeType: [],
    });
  }, [setPaymentQueryFilters]);

  const handleRemoveChip = useCallback((filterKey: string, value: string) => {
    if (filterKey === 'status') {
      setPaymentQueryFilter('paymentStatus', statusFilter.filter((v) => v !== value));
    } else if (filterKey === 'chargeType') {
      setPaymentQueryFilter('paymentChargeType', chargeTypeFilter.filter((v) => v !== value));
    }
  }, [statusFilter, chargeTypeFilter, setPaymentQueryFilter]);

  const unifiedChips: FilterChipEntry[] = useMemo(() => {
    return [
      ...statusFilter.map((value) => ({ filterKey: 'status', value })),
      ...chargeTypeFilter.map((value) => ({ filterKey: 'chargeType', value })),
    ]
      .map((s) => {
        const opt = allOptionsMap[s.filterKey]?.[s.value];
        return {
          filterKey: s.filterKey,
          value: s.value,
          label: opt?.label ?? s.value,
          icon: opt?.icon,
          iconColor: opt?.iconColor,
          onRemove: (val: string) => handleRemoveChip(s.filterKey, val),
        };
      });
  }, [statusFilter, chargeTypeFilter, allOptionsMap, handleRemoveChip]);

  const summaryItems = useMemo(() => {
    const totals = (Object.entries(summary.totalsByCurrency) as Array<[PaymentCurrency, number]>)
      .filter(([, value]) => value > 0)
      .map(([currency, value]) => ({
        label: `Total pago (${currency}):`,
        value: formatCurrency(value, currency),
      }));

    if (totals.length === 0) {
      totals.push({
        label: 'Total pago:',
        value: formatCurrency(0, 'BRL'),
      });
    }

    totals.push({
      label: 'Último pagamento:',
      value: summary.lastPaymentDate ? formatDateTime(summary.lastPaymentDate) : '—',
    });

    return totals;
  }, [summary]);

  const fetchHistory = useCallback((delayMs = 0) => {
    if (pendingDelayRef.current) clearTimeout(pendingDelayRef.current);
    if (delayMs > 0) {
      pendingDelayRef.current = setTimeout(() => {
        pendingDelayRef.current = null;
        silentRefresh();
      }, delayMs);
    } else {
      silentRefresh();
    }
  }, [silentRefresh]);

  useEffect(() => {
    return () => { if (pendingDelayRef.current) clearTimeout(pendingDelayRef.current); };
  }, []);

  useEffect(() => {
    if (!subscription?.external_subscription_id) {
      setSubscriptionsLoading(false);
      return;
    }

    const controller = new AbortController();
    setSubscriptionsLoading(true);
    PaymentGatewayService.getExternalSubscriptions(
      account.payment_system as GatewayProvider,
      subscription.external_subscription_id,
      controller.signal,
    )
      .then(setSubscriptions)
      .catch((err) => {
        const isAbort = err?.name === 'AbortError'
          || (typeof err?.message === 'string' && err.message.toLowerCase().includes('signal is aborted'));
        if (!isAbort) setSubscriptions([]);
      })
      .finally(() => { if (!controller.signal.aborted) setSubscriptionsLoading(false); });

    return () => controller.abort();
  }, [account.payment_system, subscription?.external_subscription_id]);

  const gatewaySubscription = subscriptions.find(
    (s) => s.external_subscription_id === subscription?.external_subscription_id,
  ) ?? null;
  const activeSub = gatewaySubscription?.status.toUpperCase() === 'ACTIVE'
    ? gatewaySubscription
    : subscriptions.find(
      (s) => s.status.toUpperCase() === 'ACTIVE' && s.external_subscription_id === subscription?.external_subscription_id,
    ) ?? null;
  const subscriptionSnapshotDescription = getSubscriptionSnapshotDescription(subscription);
  const subscriptionPlanName = subscription?.plan_name?.trim() || null;
  const subscriptionValueCents = subscription
    ? Math.max(subscription.price_cents - subscription.discount_cents, 0)
    : 0;
  const subscriptionDisplayDescription = activeSub?.description
    ?? subscriptionSnapshotDescription
    ?? subscriptionPlanName;
  const gatewayProvider = useMemo<GatewayProvider | null>(() => {
    return isGatewayProvider(account.payment_system) ? account.payment_system : null;
  }, [account.payment_system]);
  const isPixAccount = account.payment_system === 'pix';
  const planTransitionProvider = gatewayProvider ?? (isPixAccount ? 'pix' as const : null);
  const isCanceledSubscription = isStudentSubscriptionCanceled(subscription);
  const isActiveSubscription = isStudentSubscriptionActive(subscription);
  const gatewayNextChargeDate = gatewaySubscription?.nextChargeDate ?? null;
  const effectivePlanTransitionStartDate = useMemo(
    () => resolvePlanTransitionEffectiveStartDate(subscription),
    [subscription],
  );
  const canSchedulePlanTransition = Boolean(
    subscription?.id
    && planTransitionProvider
    && isActiveSubscription
    && !scheduledTransition
    && !activePause
    && canManagePayments
  );
  const canCancelScheduledTransition = Boolean(
    subscription?.id
    && planTransitionProvider
    && scheduledTransition?.status === 'scheduled'
    && canManageSubscriptionPlans(roles),
  );

  const canOfferChargeCheckout = canManagePayments
    && gatewayProvider === 'asaas'
    && Boolean(account.external_customer_id?.trim())
    && Boolean(subscription && isActiveSubscription && subscription.plan_id)
    && !activePause;

  const canGenerateSubscriptionCharge = Boolean(
    subscription
    && isActiveSubscription
    && subscription.external_subscription_id
    && !isPixAccount
    && !activePause
    && canManagePayments,
  );

  const loadAllHistory = useCallback(async () => {
    try {
      const data = await PaymentHistoryService.findByPaymentAccountId(account.id, null);
      setAllHistory(data);
    } catch {
      setAllHistory([]);
    }
  }, [account.id]);

  useEffect(() => {
    loadAllHistory();
  }, [loadAllHistory]);

  useEffect(() => {
    if (!subscription?.id || !planTransitionProvider) {
      setScheduledTransition(null);
      setScheduledTransitionLoading(false);
      return;
    }

    let canceled = false;
    setScheduledTransitionLoading(true);
    PaymentGatewayService.getScheduledPlanTransition(planTransitionProvider, subscription.id)
      .then((transition) => {
        if (!canceled) {
          setScheduledTransition(transition);
        }
      })
      .catch(() => {
        if (!canceled) {
          setScheduledTransition(null);
        }
      })
      .finally(() => {
        if (!canceled) {
          setScheduledTransitionLoading(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, [planTransitionProvider, subscription?.id]);

  useEffect(() => {
    setActivePause(subscription?.active_pause ?? null);
  }, [subscription?.active_pause, subscription?.id]);

  const nextChargeDate = useMemo(
    () => gatewayNextChargeDate ?? resolveUpcomingChargeDate(
      allHistory,
      subscription?.billing_cycle ?? null,
      subscription,
    ),
    [gatewayNextChargeDate, allHistory, subscription],
  );

  const handleSubscriptionConfirm = async (
    plan: SubscriptionPlan,
    selectedGateway: GatewayProvider | 'pix',
    paymentMethod: 'pix' | 'credit_card' | null,
  ) => {
    if (selectedGateway === 'asaas') {
      const cpfError = validateCpf(studentCpf, { required: true });
      if (cpfError) {
        setWarning(`CPF obrigatório para criar assinatura no Asaas: ${cpfError}`);
        setIsSubscriptionModalOpen(false);
        return;
      }
      if (paymentMethod !== 'pix' && paymentMethod !== 'credit_card') {
        setWarning('Informe a forma de pagamento (Pix ou Cartão de crédito).');
        setIsSubscriptionModalOpen(false);
        return;
      }
    }

    if (selectedGateway === 'stripe' && paymentMethod !== 'credit_card') {
      setWarning('No Stripe a forma de pagamento é Cartão de crédito.');
      setIsSubscriptionModalOpen(false);
      return;
    }

    setIsCreatingSubscription(true);
    try {
      if (selectedGateway === 'pix') {
        await PaymentGatewayService.createPixAccount(studentId, plan.id);
        fetchHistory(0);
        await onRefresh();
        await loadAllHistory();
        setIsCreatingSubscription(false);
        setIsSubscriptionModalOpen(false);
        return;
      }

      const result = await PaymentGatewayService.createSubscription(
        selectedGateway,
        '',
        plan.id,
        undefined,
        'portal',
        studentId,
        selectedGateway === 'asaas' || selectedGateway === 'stripe' ? paymentMethod : null,
      );

      const createdSubscriptionId = result.student_subscription_id;
      if (!createdSubscriptionId) {
        throw new Error('Assinatura criada no gateway, mas não foi persistida localmente.');
      }

      fetchHistory(WEBHOOK_SYNC_DELAY);
      await onRefresh();
      await loadAllHistory();

      setIsCreatingSubscription(false);
      setIsSubscriptionModalOpen(false);
    } catch (err) {
      setIsCreatingSubscription(false);
      setIsSubscriptionModalOpen(false);
      setWarning(err instanceof Error ? err.message : 'Algo deu errado ao criar a assinatura. Tente novamente.');
    }
  };

  const handlePlanTransitionConfirm = async (
    plan: SubscriptionPlan,
    effectiveStartDate: string,
    nextGateway: GatewayProvider | 'pix',
    paymentMethod: 'pix' | 'credit_card' | null,
  ) => {
    if (!subscription?.id || !planTransitionProvider) {
      setWarning('Não foi possível identificar a assinatura atual para trocar o plano.');
      setIsPlanTransitionModalOpen(false);
      return;
    }

    if (nextGateway === 'asaas') {
      const cpfError = validateCpf(studentCpf, { required: true });
      if (cpfError) {
        setWarning(`CPF obrigatório para trocar para o Asaas: ${cpfError}`);
        setIsPlanTransitionModalOpen(false);
        return;
      }
      if (paymentMethod !== 'pix' && paymentMethod !== 'credit_card') {
        setWarning('Informe a forma de pagamento (Pix ou Cartão de crédito) para o Asaas.');
        setIsPlanTransitionModalOpen(false);
        return;
      }
    }

    setIsSchedulingPlanTransition(true);
    setWarning(null);
    try {
      const result = await PaymentGatewayService.schedulePlanTransition(
        planTransitionProvider,
        subscription.id,
        plan.id,
        effectiveStartDate,
        {
          nextGatewayProvider: nextGateway,
          nextPaymentMethod: paymentMethod,
        },
      );

      setScheduledTransition(result.transition.status === 'scheduled' ? result.transition : null);
      await onRefresh();
      await loadAllHistory();
      fetchHistory(WEBHOOK_SYNC_DELAY);
      setIsPlanTransitionModalOpen(false);
    } catch (err) {
      setWarning(err instanceof Error ? err.message : 'Não foi possível agendar a troca de plano.');
      setIsPlanTransitionModalOpen(false);
    } finally {
      setIsSchedulingPlanTransition(false);
    }
  };

  const handleCancelScheduledTransitionConfirm = async () => {
    if (!subscription?.id || !planTransitionProvider) {
      setWarning('Não foi possível identificar a assinatura atual para cancelar a troca de plano.');
      setIsCancelTransitionModalOpen(false);
      return;
    }

    setIsCancellingScheduledTransition(true);
    setWarning(null);
    try {
      await PaymentGatewayService.cancelScheduledPlanTransition(
        planTransitionProvider,
        subscription.id,
      );
      setScheduledTransition(null);
      setIsCancelTransitionModalOpen(false);
      await onRefresh();
      await loadAllHistory();
      fetchHistory(WEBHOOK_SYNC_DELAY);
    } catch (err) {
      setWarning(err instanceof Error ? err.message : 'Não foi possível cancelar a troca de plano.');
      setIsCancelTransitionModalOpen(false);
    } finally {
      setIsCancellingScheduledTransition(false);
    }
  };

  const handleCreateChargeConfirm = async () => {
    if (creatingChargeRef.current) {
      return;
    }

    if (!subscription?.id) {
      setWarning('Não é possível gerar cobrança sem assinatura ativa.');
      setIsCreateChargeModalOpen(false);
      return;
    }

    if (activePause) {
      setWarning('Não é possível gerar cobrança enquanto a assinatura estiver pausada.');
      setIsCreateChargeModalOpen(false);
      return;
    }

    creatingChargeRef.current = true;
    setIsCreatingCharge(true);
    setWarning(null);
    try {
      await PaymentGatewayService.createSubscriptionCharge(
        account.payment_system as GatewayProvider,
        account.id,
        subscription.id,
      );

      setIsCreateChargeModalOpen(false);
      fetchHistory(WEBHOOK_SYNC_DELAY);
      void loadAllHistory();
      void silentRefresh();
    } catch (err) {
      setIsCreateChargeModalOpen(false);
      setWarning(err instanceof Error ? err.message : 'Não foi possível gerar a cobrança.');
    } finally {
      creatingChargeRef.current = false;
      setIsCreatingCharge(false);
    }
  };

  const handleRegisterPixCharge = async (input: { status: PixChargeStatus; chargeDate: string }) => {
    if (!subscription?.id || !isPixAccount) {
      setWarning('Não é possível registrar cobrança sem assinatura ativa de Pix direto na conta.');
      setIsPixChargeModalOpen(false);
      return;
    }

    setIsRegisteringPixCharge(true);
    setWarning(null);
    try {
      await PaymentGatewayService.registerPixCharge(subscription.id, input.status, input.chargeDate);
      setIsPixChargeModalOpen(false);
      fetchHistory(0);
      void loadAllHistory();
      void silentRefresh();
    } catch (err) {
      setWarning(err instanceof Error ? err.message : 'Não foi possível registrar a cobrança.');
    } finally {
      setIsRegisteringPixCharge(false);
    }
  };

  const handleCreatePixSubscription = async (selection: CreatePaymentAccountSelection) => {
    if (selection.system !== 'pix') {
      return;
    }

    setIsCreatingPixSubscription(true);
    setWarning(null);
    try {
      await PaymentGatewayService.createPixAccount(studentId, selection.planId);
      setIsPixPlanModalOpen(false);
      await onRefresh();
      fetchHistory(0);
      void loadAllHistory();
    } catch (err) {
      setWarning(err instanceof Error ? err.message : 'Não foi possível criar a assinatura de Pix direto na conta.');
    } finally {
      setIsCreatingPixSubscription(false);
    }
  };

  const handleCancelSubscriptionConfirm = async () => {
    if (!subscription || (!isPixAccount && !subscription.external_subscription_id)) return;
    setIsCancellingSubscription(true);
    try {
      if (isPixAccount) {
        await PaymentGatewayService.cancelPixSubscription(
          subscription.id,
          subscriptionCancellationReason,
          subscriptionCancellationObservation,
        );
      } else if (subscription.external_subscription_id) {
        await PaymentGatewayService.cancelSubscription(
          subscription.id,
          account.payment_system as GatewayProvider,
          subscription.external_subscription_id,
          subscriptionCancellationReason,
          subscriptionCancellationObservation,
        );
      }
      setSubscriptions((prev) =>
        prev.map((s) =>
          s.external_subscription_id === subscription.external_subscription_id
            ? { ...s, status: 'canceled' }
            : s
        )
      );
      await onRefresh();
      setIsCancellingSubscription(false);
      setIsCancelModalOpen(false);
      setSubscriptionCancellationReason('');
      setSubscriptionCancellationObservation(null);
    } catch (err) {
      setIsCancellingSubscription(false);
      setIsCancelModalOpen(false);
      setSubscriptionCancellationReason('');
      setSubscriptionCancellationObservation(null);
      setWarning(err instanceof Error ? err.message : 'Algo deu errado ao cancelar a assinatura. Tente novamente.');
    }
  };

  const handleCancelReasonProceed = (reason: string, observation: string | null) => {
    setSubscriptionCancellationReason(reason);
    setSubscriptionCancellationObservation(observation);
    setIsCancelReasonModalOpen(false);
    setIsCancelModalOpen(true);
  };

  const handlePauseSubscriptionConfirm = async ({
    pausedUntil,
    reason,
  }: {
    pausedUntil: string;
    reason: string | null;
  }) => {
    if (!subscription || !gatewayProvider) {
      setWarning('Gateway não suportado para pausa.');
      return;
    }
    setIsPausingSubscription(true);
    try {
      const record = await PaymentGatewayService.pauseSubscription(
        gatewayProvider,
        subscription.id,
        pausedUntil,
        reason,
      );
      setActivePause(record);
      setIsPauseModalOpen(false);
      await onRefresh();
      fetchHistory(WEBHOOK_SYNC_DELAY);
      void loadAllHistory();
    } catch (err) {
      setIsPauseModalOpen(false);
      setWarning(err instanceof Error ? err.message : 'Não foi possível pausar a assinatura.');
    } finally {
      setIsPausingSubscription(false);
    }
  };

  const handleResumeSubscriptionConfirm = async () => {
    if (!subscription || !gatewayProvider) {
      setWarning('Gateway não suportado para retomada.');
      return;
    }
    setIsResumingSubscription(true);
    try {
      await PaymentGatewayService.resumeSubscription(
        gatewayProvider,
        subscription.id,
      );
      setActivePause(null);
      setIsResumeModalOpen(false);
      await onRefresh();
      fetchHistory(WEBHOOK_SYNC_DELAY);
      void loadAllHistory();
    } catch (err) {
      setIsResumeModalOpen(false);
      setWarning(err instanceof Error ? err.message : 'Não foi possível retomar a assinatura.');
    } finally {
      setIsResumingSubscription(false);
    }
  };

  const handleTrainingCycleChange = async (nextValue: string) => {
    if (!subscription || !isTrainingCycleValue(nextValue)) {
      return;
    }

    const current = resolveTrainingCycleValue(subscription.training_cycle);
    if (nextValue === current || isUpdatingTrainingCycle) {
      setIsEditingTrainingCycle(false);
      return;
    }

    setIsUpdatingTrainingCycle(true);
    setWarning(null);
    try {
      await StudentSubscriptionService.updateTrainingCycle(subscription.id, nextValue);
      await onRefresh();
      setIsEditingTrainingCycle(false);
    } catch (err) {
      setWarning(err instanceof Error ? err.message : 'Não foi possível atualizar o ciclo de treino.');
    } finally {
      setIsUpdatingTrainingCycle(false);
    }
  };

  const scheduledResumeDate = useMemo(() => {
    if (!activePause?.paused_until) return null;
    return getScheduledResumeDate(activePause.paused_until);
  }, [activePause?.paused_until]);

  const canOfferPause = Boolean(
    subscription?.id
    && gatewayProvider
    && isActiveSubscription
    && !scheduledTransition
    && !activePause
    && canManagePause,
  );

  const canOfferResume = Boolean(
    subscription?.id
    && gatewayProvider
    && activePause
    && canManagePause,
  );

  const extractPaymentUrl = useCallback((payload: Record<string, unknown>, provider: GatewayProvider): string | null => {
    if (provider === 'asaas') {
      const payment = (payload.payment ?? payload) as Record<string, unknown>;
      return (payment.invoiceUrl as string) || (payment.bankSlipUrl as string) || null;
    }

    if (provider === 'stripe') {
      return (payload.hosted_invoice_url as string) || null;
    }

    if (provider === 'paypal') {
      return (payload.invoiceUrl as string) || null;
    }

    return null;
  }, []);

  const mergePaymentUrlIntoPayload = useCallback((
    basePayload: Record<string, unknown> | null,
    paymentLinkUrl: string | null,
    provider: GatewayProvider,
  ): Record<string, unknown> | null => {
    const fallbackPayload = basePayload ?? null;

    if (!paymentLinkUrl) {
      return fallbackPayload;
    }

    if (provider === 'asaas') {
      const basePayload = fallbackPayload ?? {};

      if (isRecord(basePayload.payment)) {
        return {
          ...basePayload,
          payment: {
            ...(basePayload.payment as Record<string, unknown>),
            invoiceUrl: paymentLinkUrl,
          },
        };
      }

      return {
        ...basePayload,
        invoiceUrl: paymentLinkUrl,
      };
    }

    if (provider === 'stripe') {
      return {
        ...(fallbackPayload ?? {}),
        hosted_invoice_url: paymentLinkUrl,
      };
    }

    if (provider === 'paypal') {
      return {
        ...(fallbackPayload ?? {}),
        invoiceUrl: paymentLinkUrl,
      };
    }

    return fallbackPayload;
  }, []);

  const loadStoredPaymentLink = useCallback(async (ph: PaymentHistory) => {
    const record = await PaymentHistoryService.findById(ph.id);
    return record?.payment_link_url ?? ph.payment_link_url ?? null;
  }, []);

  const fetchPayload = useCallback(async (ph: PaymentHistory, provider: GatewayProvider) => {
    setPayloadLoading(true);
    try {
      let gatewayPayload: Record<string, unknown> | null = null;
      let paymentLinkUrl = ph.payment_link_url ?? null;

      try {
        const paymentDetail = await PaymentGatewayService.getPaymentDetail(
          provider,
          ph.external_payment_id,
          ph.id,
          resolvePayPalSubscriptionIdForLookup(ph, subscription),
        );

        const normalizedDetail = normalizeGatewayPaymentDetail(paymentDetail);

        gatewayPayload = normalizedDetail.payload;
        paymentLinkUrl = normalizedDetail.url ?? paymentLinkUrl;

        if (!paymentLinkUrl && gatewayPayload) {
          paymentLinkUrl = extractPaymentUrl(gatewayPayload, provider);
        }

        if (paymentLinkUrl && paymentLinkUrl !== ph.payment_link_url) {
          optimisticUpdate(ph.id, { payment_link_url: paymentLinkUrl });
        }
      } catch {
        gatewayPayload = null;
      }

      if (!paymentLinkUrl) {
        paymentLinkUrl = await loadStoredPaymentLink(ph);
      }

      if (!paymentLinkUrl && gatewayPayload) {
        paymentLinkUrl = extractPaymentUrl(
          mergePaymentUrlIntoPayload(gatewayPayload, ph.payment_link_url, provider) ?? gatewayPayload,
          provider,
        );
      }

      const fallbackPayload = buildFallbackPayloadFromPaymentHistory(ph, paymentLinkUrl);
      const basePayload = hasPersistablePayload(gatewayPayload) ? gatewayPayload : fallbackPayload;
      const payloadValue = Number(basePayload.value ?? 0);
      const resolvedPayload = {
        ...basePayload,
        ...(payloadValue <= 0 && Number(ph.value) > 0 ? { value: ph.value } : {}),
        ...(!basePayload.currency && ph.currency ? { currency: ph.currency } : {}),
      };

      setPayloadDetail(mergePaymentUrlIntoPayload(resolvedPayload, paymentLinkUrl, provider) ?? resolvedPayload);
    } catch {
      const paymentLinkUrl = ph.payment_link_url ?? await loadStoredPaymentLink(ph);
      setPayloadDetail(
        mergePaymentUrlIntoPayload(
          buildFallbackPayloadFromPaymentHistory(ph, paymentLinkUrl),
          paymentLinkUrl,
          provider,
        ),
      );
    } finally {
      setPayloadLoading(false);
    }
  }, [extractPaymentUrl, loadStoredPaymentLink, mergePaymentUrlIntoPayload, optimisticUpdate, subscription]);

  const handleCancelPaymentConfirm = async () => {
    if (!paymentToCancel) return;
    const cancelled = paymentToCancel;
    setIsCancellingPayment(false);
    setPaymentToCancel(null);
    try {
      await PaymentGatewayService.cancelPayment(
        account.payment_system as GatewayProvider,
        cancelled.external_payment_id,
      );
      setAllHistory((prev) => prev.map((payment) => (
        payment.id === cancelled.id
          ? { ...payment, status: 'CANCELED' }
          : payment
      )));
      optimisticUpdate(cancelled.id, { status: 'CANCELED' });
      silentRefresh();
      void loadAllHistory();
    } catch (err) {
      setWarning(err instanceof Error ? err.message : 'Não foi possível cancelar a cobrança.');
    }
  };

  const handleDeletePixChargeConfirm = async () => {
    if (!paymentToDelete) return;
    const deleted = paymentToDelete;
    setIsDeletingPayment(true);
    try {
      await PaymentGatewayService.deletePixCharge(deleted.id);
      setAllHistory((prev) => prev.filter((payment) => payment.id !== deleted.id));
      setPaymentToDelete(null);
      silentRefresh();
      void loadAllHistory();
    } catch (err) {
      setWarning(err instanceof Error ? err.message : 'Não foi possível excluir a cobrança.');
    } finally {
      setIsDeletingPayment(false);
    }
  };

  const handleConfirmPayment = async (paidAtIso: string) => {
    if (!paymentToConfirm) {
      return;
    }

    const confirmed = paymentToConfirm;
    setIsConfirmingPayment(true);
    try {
      const result = await PaymentGatewayService.confirmPortalPayment(confirmed.id, paidAtIso);
      setAllHistory((prev) => prev.map((payment) => (
        payment.id === confirmed.id
          ? { ...payment, status: 'PAID', paid_at: result.paid_at }
          : payment
      )));
      optimisticUpdate(confirmed.id, { status: 'PAID', paid_at: result.paid_at });
      setPaymentToConfirm(null);
      silentRefresh();
      void loadAllHistory();
    } catch (err) {
      setWarning(err instanceof Error ? err.message : 'Não foi possível confirmar o pagamento.');
    } finally {
      setIsConfirmingPayment(false);
    }
  };

  const handleRefundPaymentConfirm = async () => {
    if (!paymentToRefund) return;

    const provider = gatewayProvider;
    if (!provider) {
      setWarning('Sistema de pagamento da conta não é suportado para estorno.');
      return;
    }

    const refunded = paymentToRefund;
    setIsRefundingPayment(true);
    try {
      await PaymentGatewayService.refundPayment(
        provider,
        refunded.external_payment_id,
        refunded.id,
      );
      setAllHistory((prev) => prev.map((payment) => (
        payment.id === refunded.id
          ? { ...payment, status: 'REFUNDED' }
          : payment
      )));
      optimisticUpdate(refunded.id, { status: 'REFUNDED' });
      setPaymentToRefund(null);
      silentRefresh();
      void loadAllHistory();
    } catch (err) {
      setWarning(err instanceof Error ? err.message : 'Não foi possível estornar a cobrança.');
    } finally {
      setIsRefundingPayment(false);
    }
  };

  const handleViewDetail = async (ph: PaymentHistory) => {
    if (isPixAccount) {
      setWarning(null);
      setPayloadDetail(null);
      setPayloadLoading(false);
      setSelectedPayment(ph);
      return;
    }

    const provider = gatewayProvider;
    if (!provider) {
      setWarning('Sistema de pagamento da conta não é suportado para consulta no gateway.');
      return;
    }

    setWarning(null);
    setSelectedPayment(ph);
    await fetchPayload(ph, provider);
  };

  const resolvePaymentLinkUrl = async (ph: PaymentHistory): Promise<{
    url: string | null;
    errorMessage: string | null;
    approvalLinkRefreshed: boolean;
  }> => {
    const provider = gatewayProvider;
    if (!provider) {
      return {
        url: null,
        errorMessage: 'Sistema de pagamento da conta não é suportado para consultar o link.',
        approvalLinkRefreshed: false,
      };
    }

    const shouldForceGatewayRefresh = provider === 'paypal';
    const paypalSubscriptionId = provider === 'paypal'
      ? resolvePayPalSubscriptionIdForLookup(ph, subscription)
      : null;

    let url = shouldForceGatewayRefresh ? null : (ph.payment_link_url ?? null);
    let nextPayload: Record<string, unknown> | null = null;
    let gatewayErrorMessage: string | null = null;
    let approvalLinkRefreshed = false;

    if (!url || shouldForceGatewayRefresh) {
      try {
        const paymentLinkResult = await PaymentGatewayService.getPaymentDetail(
          provider,
          ph.external_payment_id,
          ph.id,
          paypalSubscriptionId,
          provider === 'paypal',
        );

        approvalLinkRefreshed = paymentLinkResult.approval_link_refreshed === true;
        const normalizedDetail = normalizeGatewayPaymentDetail(paymentLinkResult);
        url = normalizedDetail.url ?? null;
        nextPayload = normalizedDetail.payload;

        const refreshedExternalPaymentId = paymentLinkResult.external_payment_id;
        if (url) {
          optimisticUpdate(ph.id, {
            payment_link_url: url,
            ...(approvalLinkRefreshed && refreshedExternalPaymentId
              ? {
                external_payment_id: refreshedExternalPaymentId,
                external_subscription_id: refreshedExternalPaymentId,
                external_created_date: new Date().toISOString().split('T')[0],
              }
              : {}),
          });
        }
      } catch (err) {
        gatewayErrorMessage = err instanceof Error ? err.message : 'Não foi possível consultar o link no gateway.';
        nextPayload = null;
      }
    }

    if (!url && nextPayload) {
      url = extractPaymentUrl(nextPayload, provider);
    }

    const paypalPendingApproval = provider === 'paypal'
      && isPayPalPendingApprovalCharge(ph, nextPayload);

    if (!url && !paypalPendingApproval) {
      const storedPaymentLinkUrl = await loadStoredPaymentLink(ph);
      url = storedPaymentLinkUrl ?? ph.payment_link_url ?? null;
    }

    if (!url && nextPayload) {
      url = extractPaymentUrl(
        mergePaymentUrlIntoPayload(nextPayload, ph.payment_link_url, provider) ?? nextPayload,
        provider,
      );
    }

    if (!url) {
      if (provider === 'paypal' && subscription?.is_single_payment && /^I-/i.test(ph.external_payment_id)) {
        return {
          url: null,
          errorMessage: 'Essa cobrança PayPal foi criada no fluxo antigo de assinatura e precisa ser recriada para gerar um link único válido.',
          approvalLinkRefreshed,
        };
      }

      if (paypalPendingApproval || isPayPalApprovalPending(nextPayload)) {
        return {
          url: null,
          errorMessage: PAYPAL_APPROVAL_LINK_EXPIRED_MESSAGE,
          approvalLinkRefreshed,
        };
      }

      return {
        url: null,
        errorMessage: gatewayErrorMessage ?? 'Link de pagamento não disponível para esta cobrança.',
        approvalLinkRefreshed,
      };
    }

    if (selectedPayment?.id === ph.id && nextPayload && Object.keys(nextPayload).length > 0) {
      setPayloadDetail(mergePaymentUrlIntoPayload(nextPayload, url, provider) ?? nextPayload);
    }

    return { url, errorMessage: null, approvalLinkRefreshed };
  };

  const handleCopyLink = async (ph: PaymentHistory) => {
    if (copyingPaymentId === ph.id) {
      return;
    }

    setCopyingPaymentId(ph.id);
    setWarning(null);

    try {
      const result = await resolvePaymentLinkUrl(ph);
      if (!result.url) {
        setWarning(result.errorMessage ?? 'Link de pagamento não disponível para esta cobrança.');
        return;
      }

      await copyTextToClipboard(result.url);
      setCopiedPaymentId(ph.id);
      if (result.approvalLinkRefreshed) {
        setWarning(PAYPAL_APPROVAL_LINK_REFRESHED_MESSAGE);
        void loadAllHistory();
        void silentRefresh();
      }
      setTimeout(() => setCopiedPaymentId((prev) => (prev === ph.id ? null : prev)), 2500);
    } catch {
      setWarning('Não foi possível copiar o link de pagamento.');
    } finally {
      setCopyingPaymentId(null);
    }
  };

  const handleCopyReceiptLink = async (ph: PaymentHistory) => {
    if (copyingPaymentId === ph.id) {
      return;
    }

    const provider = gatewayProvider;
    if (!provider) {
      setWarning('Sistema de pagamento da conta não é suportado para consultar o comprovante.');
      return;
    }

    setCopyingPaymentId(ph.id);
    setWarning(null);

    try {
      const paymentDetail = await PaymentGatewayService.getPaymentDetail(
        provider,
        ph.external_payment_id,
        ph.id,
        provider === 'paypal' ? resolvePayPalSubscriptionIdForLookup(ph, subscription) : null,
      );
      const receiptUrl = extractTransactionReceiptUrl(
        normalizeGatewayPaymentDetail(paymentDetail).payload,
      );

      if (!receiptUrl) {
        setWarning('Comprovante não disponível para esta cobrança.');
        return;
      }

      await copyTextToClipboard(receiptUrl);
      setCopiedPaymentId(ph.id);
      setTimeout(() => setCopiedPaymentId((prev) => (prev === ph.id ? null : prev)), 2500);
    } catch {
      setWarning('Não foi possível copiar o link do comprovante.');
    } finally {
      setCopyingPaymentId(null);
    }
  };

  return (
    <div>
      {warning && <div className={styles['alert-warning']}>{warning}</div>}

      <div className={styles['top-section']}>
        <div className={styles['account-panel']}>
          <div className={styles['section-header']}>
            <span className={styles['section-label']}>CONTA DE PAGAMENTO</span>
            {/* Botão "Alterar conta de pagamento" oculto de propósito por enquanto.
            {canChangeAccount && (
              <Button
                variant="outline"
                size="small"
                onClick={onChangeAccount}
              >
                Alterar conta de pagamento
              </Button>
            )} */}
          </div>
          <div className={styles['account-info-grid']}>
            <div className={styles['account-info-col']}>
              <div className={styles['info-line']}>
                <span className={styles['info-label']}>Pagador: </span>
                {account.name ?? '—'}
              </div>
              <div className={styles['info-line']}>
                <span className={styles['info-label']}>E-mail do pagador: </span>
                {account.email ?? '—'}
              </div>
            </div>
            <div className={styles['account-info-col']}>
              <div className={styles['info-line']}>
                <span className={styles['info-label']}>ID Externo: </span>
                {isPixAccount ? '—' : account.external_customer_id}
              </div>
              <div className={styles['info-line']}>
                <span className={styles['info-label']}>Sistema: </span>
                {PAYMENT_SYSTEM_LABELS[account.payment_system] ?? account.payment_system}
              </div>
              <div className={styles['info-line']}>
                <span className={styles['info-label']}>Criado em: </span>
                {formatDate(account.created_at)}
              </div>
            </div>
          </div>
        </div>

        <div className={styles['vertical-divider']} />

        <div className={styles['subscription-panel']}>
          <div className={styles['section-header']}>
            <div className={styles['section-title']}>
              <span className={styles['section-label']}>ASSINATURA</span>
              <SubscriptionHeaderActions
                onOpenPaymentLink={
                  subscription?.external_subscription_id && canOfferChargeCheckout
                    ? () => { setWarning(null); setIsChargeCheckoutModalOpen(true); }
                    : undefined
                }
                onOpenPlanTransition={
                  canSchedulePlanTransition
                    ? () => { setWarning(null); setIsPlanTransitionModalOpen(true); }
                    : undefined
                }
                onOpenResume={
                  canOfferResume
                    ? () => { setWarning(null); setIsResumeModalOpen(true); }
                    : undefined
                }
                onOpenPause={
                  canOfferPause
                    ? () => { setWarning(null); setIsPauseModalOpen(true); }
                    : undefined
                }
              />
              <StudentConsultancyCreditControl
                studentId={studentId}
                canManage={canManageConsultancyCredit}
                variant="headerIcon"
                iconButtonClassName={styles['btn-section-icon']}
                onChanged={() => {
                  void onRefresh();
                }}
              />
            </div>
          </div>

          {subscriptionsLoading ? (
            <Loading />
          ) : (subscription && isActiveSubscription) ? (
            <>
              {scheduledTransition && (
                <SubscriptionStatusCard
                  title={TRANSITION_STATUS_LABELS[scheduledTransition.status] ?? 'Troca programada'}
                  headerContent={(
                    <div className={styles['subscription-status-header-meta']}>
                      <span className={styles['subscription-status-meta']}>
                        {scheduledTransition.status === 'processing'
                          ? 'Executando agora'
                          : `Inicia em ${formatDate(scheduledTransition.effective_start_date)}`}
                      </span>
                      {canCancelScheduledTransition && (
                        <Tooltip content="Cancelar agendamento">
                          <Button
                            type="button"
                            variant="link"
                            size="xs"
                            icon="close"
                            iconSize={16}
                            className={styles['btn-section-icon']}
                            aria-label="Cancelar agendamento"
                            onClick={() => { setWarning(null); setIsCancelTransitionModalOpen(true); }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  )}
                >
                  <span>
                    Novo plano: <strong>{scheduledTransition.next_plan_name}</strong>
                  </span>
                  <span>
                    Gateway: <strong>{getGatewayLabel(scheduledTransition.gateway_provider)}</strong>
                  </span>
                </SubscriptionStatusCard>
              )}
              {scheduledTransitionLoading && !scheduledTransition && (
                <div className={styles['scheduled-transition-loading']}>
                  Verificando troca programada...
                </div>
              )}
              {activePause && (
                <SubscriptionStatusCard
                  title="Assinatura pausada"
                  headerContent={(
                    <span className={styles['subscription-status-meta']}>
                      Até {formatDate(activePause.paused_until)}
                    </span>
                  )}
                >
                  <span>
                    Retorno automático em <strong>{scheduledResumeDate ? formatDate(scheduledResumeDate) : '—'}</strong>
                  </span>
                  {activePause.reason && (
                    <span>Motivo: <strong>{activePause.reason}</strong></span>
                  )}
                </SubscriptionStatusCard>
              )}
              <ContentCard>
                <ContentCard.Grid>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Nome do plano:</span>
                    <span className={styles['plan-name-row']}>
                      <PlanNameWithProject
                        name={subscriptionPlanName}
                        project={subscription.project}
                        nameClassName={styles['detail-value']}
                      />
                    </span>
                    <span className={styles['detail-value']}>
                      {formatLoyaltyPlanLabel(subscription.loyalty_months)}
                    </span>
                  </div>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label-row']}>
                      <span className={styles['detail-label']}>Ciclo de treino:</span>
                      {canManagePayments && (
                        <Tooltip content={isEditingTrainingCycle ? 'Cancelar edição' : 'Editar ciclo de treino'}>
                          <Button
                            type="button"
                            variant="link"
                            size="xs"
                            icon={isEditingTrainingCycle ? 'close' : 'edit'}
                            iconSize={16}
                            className={styles['btn-section-icon']}
                            aria-label={isEditingTrainingCycle ? 'Cancelar edição do ciclo de treino' : 'Editar ciclo de treino'}
                            aria-expanded={isEditingTrainingCycle}
                            disabled={isUpdatingTrainingCycle}
                            onClick={() => {
                              setWarning(null);
                              setIsEditingTrainingCycle((current) => !current);
                            }}
                          />
                        </Tooltip>
                      )}
                    </span>
                    {canManagePayments && isEditingTrainingCycle ? (
                      <FormSelect
                        id="subscription-training-cycle"
                        options={TRAINING_CYCLE_OPTIONS}
                        value={resolveTrainingCycleValue(subscription.training_cycle)}
                        onChange={(event) => {
                          void handleTrainingCycleChange(event.target.value);
                        }}
                        disabled={isUpdatingTrainingCycle}
                        allowEmpty={false}
                        containerClassName={styles['training-cycle-select']}
                      />
                    ) : (
                      <span className={styles['detail-value']}>
                        {getTrainingCycleLabel(subscription.training_cycle)}
                      </span>
                    )}
                  </div>
                  {subscriptionSnapshotDescription && (
                    <div className={styles['detail-group']}>
                      <span className={styles['detail-label']}>Descrição contratada:</span>
                      <span className={styles['detail-value']}>{subscriptionSnapshotDescription}</span>
                    </div>
                  )}
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Data de início:</span>
                    <span className={styles['detail-value']}>
                      {subscription.start_date ? formatDate(subscription.start_date) : '—'}
                    </span>
                  </div>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Criado por:</span>
                    <span className={styles['detail-value']}>
                      {subscription.created_by_name?.trim() || '—'}
                    </span>
                  </div>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Valor mensal:</span>
                    <span className={styles['detail-value']}>
                      {formatCurrency(subscriptionValueCents / 100)}
                    </span>
                  </div>
                  {subscription.payment_method && (
                    <div className={styles['detail-group']}>
                      <span className={styles['detail-label']}>Forma de pagamento:</span>
                      <span className={styles['detail-value']}>
                        {SUBSCRIPTION_PAYMENT_METHOD_LABELS[subscription.payment_method]
                          ?? subscription.payment_method}
                      </span>
                    </div>
                  )}
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Fidelidade:</span>
                    <span className={styles['detail-value']}>
                      {formatLoyaltyProgressLabel(subscription.start_date, subscription.loyalty_months)}
                    </span>
                  </div>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Próxima cobrança:</span>
                    <span className={styles['detail-value']}>
                      {nextChargeDate ? formatDate(nextChargeDate) : 'Sem cobrança futura agendada'}
                    </span>
                  </div>
                </ContentCard.Grid>
              </ContentCard>
            </>
          ) : subscription ? (
            <>
              <ContentCard variant="danger">
                <ContentCard.Grid>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Status:</span>
                    <span className={styles['detail-value']}>
                      {isCanceledSubscription ? 'Cancelada' : 'Encerrada'}
                    </span>
                  </div>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Nome do plano:</span>
                    <span className={styles['plan-name-row']}>
                      <PlanNameWithProject
                        name={subscriptionPlanName}
                        project={subscription.project}
                        nameClassName={styles['detail-value']}
                      />
                    </span>
                    <span className={styles['detail-value']}>
                      {formatLoyaltyPlanLabel(subscription.loyalty_months)}
                    </span>
                  </div>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Ciclo de treino:</span>
                    <span className={styles['detail-value']}>
                      {getTrainingCycleLabel(subscription.training_cycle)}
                    </span>
                  </div>
                  {subscriptionSnapshotDescription && (
                    <div className={styles['detail-group']}>
                      <span className={styles['detail-label']}>Descrição contratada:</span>
                      <span className={styles['detail-value']}>{subscriptionSnapshotDescription}</span>
                    </div>
                  )}
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Data de início:</span>
                    <span className={styles['detail-value']}>
                      {subscription.start_date ? formatDate(subscription.start_date) : '—'}
                    </span>
                  </div>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Criado por:</span>
                    <span className={styles['detail-value']}>
                      {subscription.created_by_name?.trim() || '—'}
                    </span>
                  </div>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>Valor mensal:</span>
                    <span className={styles['detail-value']}>
                      {formatCurrency(subscriptionValueCents / 100)}
                    </span>
                  </div>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>
                      {isCanceledSubscription ? 'Data de cancelamento:' : 'Data de encerramento:'}
                    </span>
                    <span className={styles['detail-value']}>
                      {isCanceledSubscription
                        ? (subscription.canceled_at ? formatDate(subscription.canceled_at) : '—')
                        : (subscription.ended_at ? formatDate(subscription.ended_at) : '—')}
                    </span>
                  </div>
                  <div className={styles['detail-group']}>
                    <span className={styles['detail-label']}>
                      {isCanceledSubscription ? 'Motivo do cancelamento:' : 'Motivo do encerramento:'}
                    </span>
                    <span className={styles['detail-value']}>
                      {isCanceledSubscription
                        ? (subscription.cancellation_reason?.trim()
                          ? getStudentInactivationReasonLabel(subscription.cancellation_reason)
                          : '—')
                        : (subscription.ended_reason
                          ? (ENDED_REASON_LABELS[subscription.ended_reason] ?? subscription.ended_reason)
                          : '—')}
                    </span>
                  </div>
                  {isCanceledSubscription && subscription.cancellation_observation?.trim() && (
                    <div className={styles['detail-group']}>
                      <span className={styles['detail-label']}>Observação:</span>
                      <span className={styles['detail-value']}>{subscription.cancellation_observation.trim()}</span>
                    </div>
                  )}
                </ContentCard.Grid>
              </ContentCard>
            </>
          ) : (
            <EmptyState message="Nenhuma assinatura ativa encontrada." icon="credit_card_off" />
          )}
        </div>
        {canManagePayments && !subscriptionsLoading && (
          <div className={styles['subscription-actions']}>
            <Button
              variant="outline"
              size="small"
              onClick={onChangeAccount}
            >
              Vincular conta de pagamento
            </Button>
            {subscription && isActiveSubscription ? (
              <>
                {isPixAccount && (
                  <Button
                    variant="primary"
                    size="small"
                    disabled={Boolean(activePause)}
                    title={activePause ? 'Indisponível enquanto a assinatura estiver pausada.' : undefined}
                    onClick={() => { setWarning(null); setIsPixChargeModalOpen(true); }}
                  >
                    Registrar cobrança
                  </Button>
                )}
                {subscription.external_subscription_id && !isPixAccount && (
                  <Button
                    variant="primary"
                    size="small"
                    disabled={!canGenerateSubscriptionCharge}
                    title={activePause ? 'Indisponível enquanto a assinatura estiver pausada.' : undefined}
                    onClick={() => { setWarning(null); setIsCreateChargeModalOpen(true); }}
                  >
                    Gerar cobrança
                  </Button>
                )}
                {(subscription.external_subscription_id || isPixAccount) && (
                  <Button
                    variant="ghost"
                    size="small"
                    className={styles['btn-cancel-subscription']}
                    onClick={() => { setWarning(null); setIsCancelReasonModalOpen(true); }}
                  >
                    Cancelar assinatura
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="primary"
                size="small"
                onClick={() => {
                  setWarning(null);
                  if (isPixAccount && !isCanceledSubscription) {
                    setIsPixPlanModalOpen(true);
                    return;
                  }
                  setIsSubscriptionModalOpen(true);
                }}
              >
                Criar assinatura
              </Button>
            )}
          </div>
        )}
      </div>

      <div className={styles['history-section']}>
      <div className={styles.divider} />

      <CreateSubscriptionModal
        open={isSubscriptionModalOpen}
        loading={isCreatingSubscription}
        defaultGateway={isPixAccount ? 'pix' : (gatewayProvider ?? 'asaas')}
        studentCpf={studentCpf}
        onConfirm={handleSubscriptionConfirm}
        onCancel={() => setIsSubscriptionModalOpen(false)}
      />
      {subscription && planTransitionProvider && (
        <SchedulePlanTransitionModal
          open={isPlanTransitionModalOpen}
          loading={isSchedulingPlanTransition}
          gateway={planTransitionProvider}
          currentSubscription={subscription}
          suggestedStartDate={effectivePlanTransitionStartDate}
          onConfirm={handlePlanTransitionConfirm}
          onCancel={() => setIsPlanTransitionModalOpen(false)}
        />
      )}
      <ConfirmModal
        open={isCancelTransitionModalOpen}
        variant="warning"
        icon="event_busy"
        title="Cancelar troca de plano"
        message={
          scheduledTransition
            ? `A troca programada para o plano "${scheduledTransition.next_plan_name}", com início em ${formatDate(scheduledTransition.effective_start_date)}, será cancelada. A assinatura atual continua ativa e o encerramento já agendado no gateway será desfeito. Você poderá agendar outra troca depois.`
            : 'A troca de plano programada será cancelada. A assinatura atual continua ativa.'
        }
        confirmLabel={isCancellingScheduledTransition ? 'Aguarde...' : 'Cancelar agendamento'}
        cancelLabel="Voltar"
        loading={isCancellingScheduledTransition}
        onConfirm={() => { void handleCancelScheduledTransitionConfirm(); }}
        onCancel={() => {
          if (!isCancellingScheduledTransition) {
            setIsCancelTransitionModalOpen(false);
          }
        }}
      />
      <CreateChargeModal
        open={isCreateChargeModalOpen}
        loading={isCreatingCharge}
        defaultAmount={subscriptionValueCents / 100}
        planName={subscriptionPlanName}
        provider={gatewayProvider}
        autoRenew={Boolean(subscription?.auto_renew)}
        onConfirm={handleCreateChargeConfirm}
        onCancel={() => setIsCreateChargeModalOpen(false)}
      />
      <RegisterPixChargeModal
        open={isPixChargeModalOpen}
        loading={isRegisteringPixCharge}
        amount={subscriptionValueCents / 100}
        planName={subscriptionPlanName}
        onConfirm={(input) => { void handleRegisterPixCharge(input); }}
        onCancel={() => {
          if (!isRegisteringPixCharge) {
            setIsPixChargeModalOpen(false);
          }
        }}
      />
      <CreatePaymentAccountModal
        open={isPixPlanModalOpen}
        loading={isCreatingPixSubscription}
        lockedSystem="pix"
        title="Criar assinatura de Pix direto na conta"
        message="Escolha o plano. A mensalidade fica fixa nas cobranças manuais."
        confirmLabel="Criar assinatura"
        studentName={studentName}
        studentCpf={studentCpf}
        studentEmail=""
        onConfirm={(selection) => { void handleCreatePixSubscription(selection); }}
        onCancel={() => {
          if (!isCreatingPixSubscription) {
            setIsPixPlanModalOpen(false);
          }
        }}
      />
      <ChargeCheckoutLinkModal
        open={isChargeCheckoutModalOpen}
        studentName={studentName}
        studentId={studentId}
        initialPlanId={subscription?.plan_id ?? null}
        lockPlan
        lockedPlan={subscription
          ? {
              name: subscription.plan_name || subscriptionPlanName || 'Plano da assinatura',
              priceCents: subscriptionValueCents,
              billingCycle: subscription.billing_cycle,
              isSinglePayment: Boolean(subscription.is_single_payment),
              project: subscription.project,
            }
          : null}
        onClose={() => setIsChargeCheckoutModalOpen(false)}
      />
      <SubscriptionCancellationReasonModal
        open={isCancelReasonModalOpen}
        subscriptionName={subscriptionDisplayDescription}
        onProceed={handleCancelReasonProceed}
        onCancel={() => setIsCancelReasonModalOpen(false)}
      />
      {gatewayProvider && (
        <PauseSubscriptionModal
          open={isPauseModalOpen}
          loading={isPausingSubscription}
          provider={gatewayProvider}
          subscriptionPlanName={subscriptionPlanName}
          onConfirm={handlePauseSubscriptionConfirm}
          onCancel={() => {
            if (!isPausingSubscription) {
              setIsPauseModalOpen(false);
            }
          }}
        />
      )}
      {gatewayProvider && activePause && (
        <ResumeSubscriptionModal
          open={isResumeModalOpen}
          loading={isResumingSubscription}
          provider={gatewayProvider}
          pausedUntil={activePause.paused_until}
          scheduledResumeDate={scheduledResumeDate}
          onConfirm={handleResumeSubscriptionConfirm}
          onCancel={() => {
            if (!isResumingSubscription) {
              setIsResumeModalOpen(false);
            }
          }}
        />
      )}
      <ConfirmModal
        open={isCancelModalOpen}
        variant="danger"
        icon="cancel"
        title="Cancelar assinatura"
        message={`Tem certeza que deseja cancelar a assinatura${subscriptionDisplayDescription ? ` "${subscriptionDisplayDescription}"` : ''}? Isso irá cancelar todas as cobranças pendentes da aluna. Esta ação não pode ser desfeita.`}
        confirmLabel={isCancellingSubscription ? 'Aguarde...' : 'Confirmar'}
        cancelLabel="Voltar"
        loading={isCancellingSubscription}
        onConfirm={handleCancelSubscriptionConfirm}
        onCancel={() => {
          setIsCancelModalOpen(false);
          setSubscriptionCancellationReason('');
          setSubscriptionCancellationObservation(null);
        }}
      />
      <ConfirmModal
        open={!!paymentToCancel}
        variant="danger"
        icon="cancel"
        title="Cancelar cobrança"
        message={`Tem certeza que deseja cancelar a cobrança de ${paymentToCancel ? formatCurrency(paymentToCancel.value, paymentToCancel.currency) : ''}? Esta ação não pode ser desfeita.`}
        confirmLabel={isCancellingPayment ? 'Aguarde...' : 'Confirmar'}
        cancelLabel="Voltar"
        loading={isCancellingPayment}
        onConfirm={handleCancelPaymentConfirm}
        onCancel={() => setPaymentToCancel(null)}
      />
      <ConfirmModal
        open={!!paymentToDelete}
        variant="danger"
        icon="delete"
        title="Excluir cobrança"
        message={`Tem certeza que deseja apagar a cobrança de ${paymentToDelete ? formatCurrency(paymentToDelete.value, paymentToDelete.currency) : ''}? Esta ação não pode ser desfeita.`}
        confirmLabel={isDeletingPayment ? 'Aguarde...' : 'Excluir'}
        cancelLabel="Voltar"
        loading={isDeletingPayment}
        onConfirm={() => {
          void handleDeletePixChargeConfirm();
        }}
        onCancel={() => {
          if (!isDeletingPayment) {
            setPaymentToDelete(null);
          }
        }}
      />
      <ConfirmPaymentModal
        open={!!paymentToConfirm}
        loading={isConfirmingPayment}
        payment={paymentToConfirm}
        onConfirm={(paidAtIso) => {
          void handleConfirmPayment(paidAtIso);
        }}
        onCancel={() => {
          if (!isConfirmingPayment) {
            setPaymentToConfirm(null);
          }
        }}
      />
      <ConfirmModal
        open={!!paymentToRefund}
        variant="danger"
        icon="undo"
        title="Estornar cobrança"
        message={`Tem certeza que deseja estornar a cobrança de ${paymentToRefund ? formatCurrency(paymentToRefund.value, paymentToRefund.currency) : ''}? Esta ação não pode ser desfeita.`}
        confirmLabel={isRefundingPayment ? 'Aguarde...' : 'Estornar'}
        cancelLabel="Voltar"
        loading={isRefundingPayment}
        onConfirm={() => {
          void handleRefundPaymentConfirm();
        }}
        onCancel={() => {
          if (!isRefundingPayment) {
            setPaymentToRefund(null);
          }
        }}
      />

      {(pagination.totalRecords > 0 || historyLoading) && (
        <>
          <div className={styles['section-label']}>RESUMO</div>

          <SummaryPills items={summaryItems} />
        </>
      )}

      <div className={styles.divider} />
      <div className={styles['section-label']}>HISTÓRICO DE PAGAMENTOS</div>

      <FilterBar gap="sm" pushFirstChild>
        <FilterGroup>
          <SearchFilterBar
            searchValue={searchTerm}
            onSearchChange={(value) => setPaymentQueryFilter('paymentSearch', value)}
            searchPlaceholder="Pesquisar"
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={statusFilter}
            onChange={handleStatusFilterChange}
            options={statusOptions}
            placeholder="Status"
            hideChips
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={chargeTypeFilter}
            onChange={handleChargeTypeFilterChange}
            options={chargeTypeOptions}
            placeholder="Tipo"
            hideChips
          />
        </FilterGroup>
        {hasActiveFilters && <ClearFiltersButton onClick={handleClearFilters} />}
      </FilterBar>
      <FilterChipsBar chips={unifiedChips} />

      {historyLoading ? (
        <Loading />
      ) : sortedHistory.length === 0 ? (
        <EmptyState message="Nenhum pagamento encontrado." icon="receipt_long" />
      ) : (
        <>
          <DataTable
            className={styles['history-table']}
            headers={['Vencimento', 'Data pagamento', 'Valor', 'Status', 'Tipo', 'Detalhes da cobrança', 'Ações']}
            gridTemplate="110px 150px 130px 110px 120px minmax(120px, 1fr) auto"
            highlightTerm={searchTerm}
            borderedRows
            boldHeaders
          >
            {sortedHistory.map((ph: PaymentHistory) => {
                const normalized = normalizeStatus(ph.status);
                const statusLabel = STATUS_LABELS[normalized] ?? ph.status;
                const statusIcon = STATUS_ICON[normalized] ?? { name: 'help', color: 'var(--color-solid-info)' };
                const chargeTypeLabel = ph.charge_type ? (CHARGE_TYPE_LABELS[ph.charge_type] ?? ph.charge_type) : '—';
                const paymentCurrency = ph.currency ?? 'BRL';
                const showErrorIndicator = hasVisiblePaymentError(ph);
                const copyReceiptLink = isCanceledCharge(ph.status);
                const copyLinkLabel = copyReceiptLink ? 'Link do comprovante' : 'Copiar link';
                const copiedLabel = copyReceiptLink ? 'Copiado' : 'Copiado!';

                return (
                  <DataTable.Row key={ph.id} className={styles['history-row']}>
                    <span className={styles['cell-bold']}>{ph.due_date ? formatDate(ph.due_date) : '—'}</span>
                    <span>{ph.paid_at ? formatDateTime(ph.paid_at) : '—'}</span>
                    <span>{formatCurrency(ph.value, paymentCurrency)}</span>
                    <div className={styles['cell-status']}>
                      <Icon name={statusIcon.name} size={20} filled color={statusIcon.color} />
                      <span>{statusLabel}</span>
                    </div>
                    <span>{chargeTypeLabel}</span>
                    <div className={styles['cell-charge-detail']}>
                      <ViewLinkButton
                        label="Ver"
                        onClick={() => handleViewDetail(ph)}
                      />
                    </div>
                    <div className={styles['cell-actions']}>
                      <div className={styles['actions-cell']}>
                      {showErrorIndicator && (
                        <Tooltip content="Ver último erro da cobrança">
                          <button
                            type="button"
                            className={styles['error-indicator-button']}
                            aria-label="Ver último erro da cobrança"
                            onClick={() => {
                              setWarning(null);
                              setSelectedPaymentError(ph.latest_error ?? null);
                            }}
                          >
                            <Icon name="error" size={18} filled color="var(--color-solid-danger)" />
                          </button>
                        </Tooltip>
                      )}
                      {canManagePayments && !isPixAccount && (
                        <Tooltip content={copyLinkLabel}>
                          <Button
                            variant="ghost"
                            size="small"
                            iconSize={18}
                            icon={
                              copyingPaymentId === ph.id
                                ? 'progress_activity'
                                : copiedPaymentId === ph.id
                                  ? 'check'
                                  : 'content_copy'
                            }
                            className={[
                              styles.copyLinkButton,
                              copyingPaymentId === ph.id ? styles.copyButtonLoading : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => {
                              void (copyReceiptLink ? handleCopyReceiptLink(ph) : handleCopyLink(ph));
                            }}
                            disabled={copyingPaymentId === ph.id}
                            aria-label={copyLinkLabel}
                          >
                            {copyingPaymentId === ph.id
                              ? 'Copiando...'
                              : copiedPaymentId === ph.id
                                ? copiedLabel
                                : copyLinkLabel}
                          </Button>
                        </Tooltip>
                      )}
                      <KebabMenu
                        items={[
                          {
                            label: 'Copiar ID',
                            onClick: () => { navigator.clipboard.writeText(ph.external_payment_id); },
                          },
                          ...(canManagePayments && !isPixAccount && CONFIRMABLE_PAYMENT_STATUSES.has(normalizeStatus(ph.status))
                            ? [{
                                label: 'Confirmar pagamento',
                                onClick: () => { setWarning(null); setPaymentToConfirm(ph); },
                              }]
                            : []),
                          ...(canManagePayments && !isPixAccount && OPEN_PAYMENT_STATUSES.has(normalizeStatus(ph.status))
                            ? [{
                                label: 'Cancelar cobrança',
                                onClick: () => { setWarning(null); setPaymentToCancel(ph); },
                              }]
                            : []),
                          ...(canManagePayments && !isPixAccount && normalizeStatus(ph.status) === PAID_PAYMENT_STATUS && ph.external_payment_id
                            ? [{
                                label: 'Estornar',
                                variant: 'danger' as const,
                                onClick: () => { setWarning(null); setPaymentToRefund(ph); },
                              }]
                            : []),
                          ...(canManagePayments && isPixAccount
                            ? [{
                                label: 'Excluir cobrança',
                                variant: 'danger' as const,
                                onClick: () => { setWarning(null); setPaymentToDelete(ph); },
                              }]
                            : []),
                        ]}
                      />
                      </div>
                    </div>
                  </DataTable.Row>
                );
              })}
          </DataTable>

          <div className={styles['bottom-row']}>
            <PageSizeSelect
              value={paymentPageSize}
              options={PAYMENT_PAGE_SIZE_OPTIONS}
              onChange={(event) => {
                setPaymentQueryFilter('paymentPageSize', Number.parseInt(event.target.value, 10));
              }}
            />
            <div className={styles['pagination-center']}>
              <Pagination
                pagination={pagination}
                loading={historyLoading}
                onPageChange={(page) => {
                  setPaymentQueryFilter('paymentPage', page, { resetPage: false });
                }}
              />
            </div>
          </div>
        </>
      )}

      </div>

      <ConfirmModal
        open={!!selectedPayment}
        variant="info"
        icon="receipt_long"
        title="Detalhes da cobrança"
        hideCancel
        confirmLabel="Fechar"
        onConfirm={() => {
          setSelectedPayment(null);
          setPayloadDetail(null);
        }}
        onCancel={() => {
          setSelectedPayment(null);
          setPayloadDetail(null);
        }}
      >
        {isPixAccount && selectedPayment ? (
          <div className={styles['payment-detail-modal']}>
            <div className={styles['payment-detail-row']}>
              <span className={styles['payment-detail-label']}>Valor</span>
              <span className={styles['payment-detail-value']}>{formatCurrency(selectedPayment.value, selectedPayment.currency)}</span>
            </div>
            <div className={styles['payment-detail-row']}>
              <span className={styles['payment-detail-label']}>Status</span>
              <span className={styles['payment-detail-value']}>{STATUS_LABELS[normalizeStatus(selectedPayment.status)] ?? selectedPayment.status}</span>
            </div>
            <div className={styles['payment-detail-row']}>
              <span className={styles['payment-detail-label']}>Data</span>
              <span className={styles['payment-detail-value']}>{formatDate(selectedPayment.external_created_date)}</span>
            </div>
            <div className={styles['payment-detail-row']}>
              <span className={styles['payment-detail-label']}>Vencimento</span>
              <span className={styles['payment-detail-value']}>{selectedPayment.due_date ? formatDate(selectedPayment.due_date) : '—'}</span>
            </div>
            <div className={styles['payment-detail-row']}>
              <span className={styles['payment-detail-label']}>Pago em</span>
              <span className={styles['payment-detail-value']}>{selectedPayment.paid_at ? formatDateTime(selectedPayment.paid_at) : '—'}</span>
            </div>
            <div className={styles['payment-detail-row']}>
              <span className={styles['payment-detail-label']}>Forma</span>
              <span className={styles['payment-detail-value']}>{PAYMENT_SYSTEM_LABELS.pix}</span>
            </div>
          </div>
        ) : payloadLoading ? (
          <Loading />
        ) : payloadDetail ? (
          gatewayProvider === 'stripe'
            ? <StripePayloadView payload={payloadDetail} currentStatus={selectedPayment?.status} currentCurrency={selectedPayment?.currency} />
            : gatewayProvider === 'paypal'
              ? <PayPalPayloadView payload={payloadDetail} currentStatus={selectedPayment?.status} currentCurrency={selectedPayment?.currency} />
              : <AsaasPayloadView payload={payloadDetail} currentStatus={selectedPayment?.status} currentCurrency={selectedPayment?.currency} />
        ) : (
          <EmptyState message="Não foi possível carregar os detalhes." icon="error" />
        )}
      </ConfirmModal>
      <ConfirmModal
        open={!!selectedPaymentError}
        variant="danger"
        icon="error"
        title="Último erro da cobrança"
        hideCancel
        confirmLabel="Fechar"
        onConfirm={() => setSelectedPaymentError(null)}
        onCancel={() => setSelectedPaymentError(null)}
      >
        {selectedPaymentError ? (
          <div className={styles['payment-detail-modal']}>
            <div className={styles['payment-detail-row']}>
              <span className={styles['payment-detail-label']}>Mensagem</span>
              <span className={styles['payment-detail-value']}>{selectedPaymentError.error_message}</span>
            </div>
            <div className={styles['payment-detail-row']}>
              <span className={styles['payment-detail-label']}>Gateway</span>
              <span className={styles['payment-detail-value']}>{getGatewayLabel(selectedPaymentError.provider)}</span>
            </div>
            {selectedPaymentError.error_code && (
              <div className={styles['payment-detail-row']}>
                <span className={styles['payment-detail-label']}>Código</span>
                <span className={styles['payment-detail-value']}>{selectedPaymentError.error_code}</span>
              </div>
            )}
            {selectedPaymentError.gateway_event && (
              <div className={styles['payment-detail-row']}>
                <span className={styles['payment-detail-label']}>Evento</span>
                <span className={styles['payment-detail-value']}>{selectedPaymentError.gateway_event}</span>
              </div>
            )}
            {selectedPaymentError.raw_status && (
              <div className={styles['payment-detail-row']}>
                <span className={styles['payment-detail-label']}>Status bruto</span>
                <span className={styles['payment-detail-value']}>{selectedPaymentError.raw_status}</span>
              </div>
            )}
            {selectedPaymentError.normalized_status && (
              <div className={styles['payment-detail-row']}>
                <span className={styles['payment-detail-label']}>Status interno</span>
                <span className={styles['payment-detail-status']}>
                  <Icon
                    name={STATUS_ICON[normalizeStatus(selectedPaymentError.normalized_status)]?.name ?? 'help'}
                    size={16}
                    filled
                    color={STATUS_ICON[normalizeStatus(selectedPaymentError.normalized_status)]?.color ?? 'var(--color-solid-info)'}
                  />
                  {STATUS_LABELS[normalizeStatus(selectedPaymentError.normalized_status)] ?? selectedPaymentError.normalized_status}
                </span>
              </div>
            )}
            <div className={styles['payment-detail-row']}>
              <span className={styles['payment-detail-label']}>Registrado em</span>
              <span className={styles['payment-detail-value']}>{formatDate(selectedPaymentError.created_at)}</span>
            </div>
          </div>
        ) : (
          <EmptyState message="Nenhum erro encontrado para esta cobrança." icon="error" />
        )}
      </ConfirmModal>
    </div>
  );
};

export default PaymentDetails;
